import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Sparkles, X } from 'lucide-react';

/**
 * Popover anchored near the targeted node for entering a what-if question.
 */
const WhatIfPopover = ({ node, nodeIndex, onSubmit, onClose, isLoading }) => {
  const [question, setQuestion] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    // Auto-focus the input when popover opens
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!question.trim() || isLoading) return;
    onSubmit(nodeIndex, question.trim());
  };

  const cleanTitle = node?.title ? node.title.split(/ at /i)[0].trim() : 'this step';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -8, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -8, scale: 0.95 }}
        transition={{ duration: 0.2 }}
        className="w-full max-w-md mx-auto mb-4 bg-white rounded-2xl border-2 border-amber-200 shadow-xl overflow-hidden"
        style={{ zIndex: 100 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-amber-50 border-b border-amber-100">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-amber-600" />
            <span className="text-sm font-bold text-amber-800">What If...?</span>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-amber-100 transition-colors"
          >
            <X size={16} className="text-amber-600" />
          </button>
        </div>

        {/* Content */}
        <div className="px-4 py-3">
          <p className="text-xs text-slate-500 mb-2">
            Exploring step {nodeIndex + 1}: <span className="font-semibold text-slate-700">{cleanTitle}</span>
          </p>
          <p className="text-xs text-slate-400 mb-3">
            This will regenerate step {nodeIndex + 1} and all steps after it.
          </p>

          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            <input
              ref={inputRef}
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder={`What if I became a startup founder instead?`}
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-amber-300 bg-slate-50"
              disabled={isLoading}
            />
            <button
              type="submit"
              disabled={!question.trim() || isLoading}
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-500 text-white rounded-xl font-bold text-sm hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Generating scenario...
                </>
              ) : (
                <>
                  <Sparkles size={16} />
                  Explore This Scenario
                </>
              )}
            </button>
          </form>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default WhatIfPopover;
